import React from 'react';
import { ChevronRight } from 'lucide-react';
import { Chip } from '@cotera/client/app/components/ui';
import { Tooltip } from '@cotera/client/app/components/ui/tooltip';
import { cn } from '../../lib/utils';
import { KeyboardNavigableList } from '../ui/keyboard-navigable-list';
import { PROVIDER_ICONS } from './constants';
import { extractToolAction } from './utils';
import {
  providerReadiness,
  providerToolSummary,
  type CatalogGroup,
  type CatalogRow,
  type ProviderReadiness,
} from './catalog-model';

const ProviderIcon: React.FC<{ provider: string }> = ({ provider }) => {
  const Icon = PROVIDER_ICONS[provider as keyof typeof PROVIDER_ICONS];
  return (
    <div className="shrink-0 flex items-center justify-center w-8 h-8 rounded-md bg-muted/40 border border-border/40">
      {Icon !== undefined ? <Icon /> : null}
    </div>
  );
};

/**
 * What a row offers next: run it now, finish setting it up, or connect it.
 *
 * `partial` exists because a provider counts as connected as soon as one tool
 * runs — a plain tick there would claim every tool works.
 */
const RowStatus: React.FC<{
  readiness: ProviderReadiness;
  readyLabel: string;
  partialLabel?: string;
  /** Why this can't run, when the server told us. */
  blockedReason?: string;
}> = ({ readiness, readyLabel, partialLabel, blockedReason }) => {
  if (readiness === 'all') {
    // The tick alone carries the meaning visually; the label stays available
    // to screen readers and, via the tooltip, to anyone hovering.
    return (
      <Tooltip side="top" tooltipContent={readyLabel}>
        <Chip appearance="inline" variant="success" className="shrink-0">
          <span className="sr-only">{readyLabel}</span>
        </Chip>
      </Tooltip>
    );
  }

  if (readiness === 'partial') {
    return (
      <Tooltip side="top" tooltipContent={partialLabel ?? 'Partly configured'}>
        <Chip appearance="inline" variant="warning" className="shrink-0">
          <span className="sr-only">{partialLabel ?? 'Partly configured'}</span>
        </Chip>
      </Tooltip>
    );
  }

  // The tooltip only reaches a pointer. Repeating the reason in the accessible
  // name means screen readers get it too, and gives the behaviour something
  // stable to assert against.
  const connect = (
    <Chip appearance="inline" className="shrink-0">
      Connect
      {blockedReason !== undefined && (
        <span className="sr-only">: {blockedReason}</span>
      )}
    </Chip>
  );

  // The catalog is where you come to fix this, so say what is actually wrong
  // rather than only that something is.
  return blockedReason === undefined ? (
    connect
  ) : (
    <Tooltip side="top" tooltipContent={blockedReason}>
      {connect}
    </Tooltip>
  );
};

const CatalogRowItem: React.FC<{
  row: CatalogRow;
  /** The committed selection, whose detail fills the right pane. */
  selected: boolean;
  /** The keyboard cursor, which moves without committing. */
  active: boolean;
  navigableProps: React.HTMLAttributes<HTMLDivElement>;
}> = ({ row, selected, active, navigableProps }) => {
  const isProvider = row.t === 'provider';
  const title = isProvider
    ? row.provider.displayName
    : extractToolAction(row.tool.name, row.provider.displayName);
  const subtitle = isProvider
    ? providerToolSummary(row.provider)
    : row.provider.displayName;
  const readiness: ProviderReadiness = isProvider
    ? providerReadiness(row.provider)
    : row.tool.canExecute
    ? 'all'
    : 'none';

  return (
    <div
      {...navigableProps}
      className={cn(
        'w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left cursor-pointer transition-colors',
        selected ? 'bg-accent' : active ? 'bg-muted/70' : 'hover:bg-muted/50'
      )}
    >
      <ProviderIcon provider={row.provider.provider} />
      <span className="flex-1 min-w-0 flex flex-col">
        <span className="text-sm font-medium text-foreground truncate">
          {title}
        </span>
        <span className="text-xs text-muted-foreground truncate">
          {subtitle}
        </span>
      </span>
      <RowStatus
        readiness={readiness}
        readyLabel={isProvider ? 'Connected' : 'Ready to run'}
        partialLabel={
          isProvider
            ? `${row.provider.configuredToolCount} of ${row.provider.totalToolCount} tools ready`
            : undefined
        }
        blockedReason={isProvider ? undefined : row.tool.notExecutableReason}
      />
      <ChevronRight className="shrink-0 h-3.5 w-3.5 text-muted-foreground/40" />
    </div>
  );
};

/**
 * The catalog's left pane. Groups are visual only — every row shares one flat
 * index space so arrow keys cross group boundaries, which is also the index
 * space `KeyboardNavigableList` drives.
 */
export const CatalogList: React.FC<{
  groups: CatalogGroup[];
  /** Flattened rows, in the same order the groups render them. */
  rows: CatalogRow[];
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
  onSelectItem: (index: number) => void;
  onEscape: () => void;
  selectedRowId: string | null;
}> = ({
  groups,
  rows,
  selectedIndex,
  setSelectedIndex,
  onSelectItem,
  onEscape,
  selectedRowId,
}) => (
  <KeyboardNavigableList
    itemCount={rows.length}
    selectedIndex={selectedIndex}
    setSelectedIndex={setSelectedIndex}
    onSelectItem={onSelectItem}
    onEscape={onEscape}
    // The search field owns focus; arrow keys reach the list through its
    // handler, so stealing focus here would take the user out of the input.
    autoFocus={false}
    containerClassName="flex flex-col min-h-0 flex-1"
  >
    {({ listRef, getItemProps }) => {
      let cursor = -1;
      return (
        <div
          ref={listRef}
          id="tool-catalog-list"
          role="listbox"
          aria-label="Tools and providers"
          className="flex flex-col gap-3 flex-1 min-h-0 overflow-y-auto pr-2 pb-4"
        >
          {groups.map((group) => (
            <div
              key={group.id}
              role="group"
              aria-label={group.label}
              className="flex flex-col gap-0.5"
            >
              <div
                aria-hidden
                className="flex items-baseline justify-between px-2.5 py-1 sticky top-0 z-10 bg-background/95 backdrop-blur-sm"
              >
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {group.label}
                </span>
                {group.hint !== undefined && group.hint.length > 0 && (
                  <span className="text-[11px] tabular-nums text-muted-foreground/50">
                    {group.hint}
                  </span>
                )}
              </div>
              {group.rows.map((row) => {
                cursor += 1;
                const index = cursor;
                return (
                  <CatalogRowItem
                    key={row.id}
                    row={row}
                    selected={selectedRowId === row.id}
                    active={selectedIndex === index}
                    navigableProps={getItemProps(index, {
                      id: `tool-catalog-row-${index}`,
                    })}
                  />
                );
              })}
            </div>
          ))}
        </div>
      );
    }}
  </KeyboardNavigableList>
);
